import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { LayoutGrid, Calendar, CalendarDays, Users, Trophy, TrendingUp, Bell, ChevronRight, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const introSteps = [
  {
    icon: LayoutGrid,
    title: 'Whiteboard',
    description: "View today's Workout of the Day (WOD) and see who's attending today's classes. Rate how difficult each workout felt for you.",
    color: '#CCFF00'
  },
  {
    icon: Calendar,
    title: 'Schedule',
    description: 'Browse upcoming classes and book your spot. See who else is coming and manage your bookings. Check class capacity before booking.',
    color: '#2563EB'
  },
  {
    icon: CalendarDays,
    title: 'WOD Calendar',
    description: 'View all past and future workouts in a calendar format. Click on any WOD to see the full details and plan your training.',
    color: '#22C55E'
  },
  {
    icon: Trophy,
    title: 'Leaderboard',
    description: 'Compete with fellow athletes! View active leaderboards, submit your scores, and see where you rank against the rest of the gym.',
    color: '#F59E0B'
  },
  {
    icon: TrendingUp,
    title: 'Personal Records',
    description: 'Log and track your PRs across all movements. See your progress over time and get notified when you hit a new personal best!',
    color: '#8B5CF6'
  },
  {
    icon: Users,
    title: 'My Stats & Streak',
    description: 'Track your WOD ratings, attendance history, PRs, and weekly streak. Stay consistent and watch your progress grow!',
    color: '#EF4444'
  },
  {
    icon: Bell,
    title: 'Notifications',
    description: "Get push alerts for new classes, cancellations, a 30-min check-in reminder, and a birthday wish on your special day. Set your preferences anytime in the Notifications menu.",
    color: '#CCFF00'
  }
];

export default function AthleteIntro({ open, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleNext = () => {
    if (currentStep < introSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/mark-intro-seen`);
      toast.success('Welcome to R&R Elite Fitness!');
      onComplete();
    } catch (error) {
      toast.error('Failed to complete intro');
    } finally {
      setLoading(false);
    }
  };

  const currentIntro = introSteps[currentStep];
  const Icon = currentIntro.icon;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="bg-[#18181B] border-[#27272A] max-w-2xl" 
        data-testid="athlete-intro-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-3xl font-heading font-black uppercase text-[#FAFAFA] mb-2">
            Welcome to R&R Elite Fitness!
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA] text-base">
            Let's take a quick tour of the app to help you get started.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6">
          {/* Progress Indicators */}
          <div className="flex gap-2 mb-8">
            {introSteps.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-[#CCFF00]' : 'bg-[#27272A]'
                }`}
              />
            ))}
          </div>

          {/* Current Step Content */}
          <Card className="bg-[#09090B] border-[#27272A] p-8" data-testid={`intro-step-${currentStep}`}>
            <div className="flex flex-col items-center text-center">
              <div 
                className="p-6 rounded-sm mb-6"
                style={{ backgroundColor: `${currentIntro.color}15` }}
              >
                <Icon className="w-16 h-16" style={{ color: currentIntro.color }} />
              </div>
              
              <h3 className="text-3xl font-heading font-black uppercase text-[#FAFAFA] mb-4">
                {currentIntro.title}
              </h3>
              
              <p className="text-lg text-[#A1A1AA] leading-relaxed max-w-lg">
                {currentIntro.description}
              </p>
            </div>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8">
            <Button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              variant="outline"
              data-testid="intro-previous-button"
              className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#FAFAFA] disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="text-sm text-[#A1A1AA] font-semibold">
              {currentStep + 1} of {introSteps.length}
            </div>

            {currentStep < introSteps.length - 1 ? (
              <Button
                onClick={handleNext}
                data-testid="intro-next-button"
                className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={loading}
                data-testid="intro-complete-button"
                className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600]"
              >
                {loading ? 'Loading...' : "Let's Go!"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
